import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createCancellablePost, isRequestCancelled, type CancellableRequest } from '@/utils/request'
import UserPreferences from '@/utils/userPreferences'
import { APIWeb } from '@/api'
import CustomButton from '@/components/CustomButton'
import AcceleratePopup from '../components/accelerate-popup'
import KycFailed from '../components/kyc-failed'
import CardSwiper from '../components/card-swiper'
import CardActions, { CardActionType } from '../components/card-actions'
import TransactionRecords from '../components/transaction-records'
import type { CardData, CardSwiperRef } from '../components/card-swiper'
import type { CardListItem } from '@/hooks'
import { CardStatus } from '../index.types'

interface ElectronicCardProps {
  /** 虚拟卡列表（从父组件传入） */
  cards: CardListItem[]
}

const ElectronicCard: React.FC<ElectronicCardProps> = ({ cards }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const swiperRef = useRef<CardSwiperRef>(null)

  // 本地卡片数据，用于更新余额
  const [localCards, setLocalCards] = useState<CardData[]>(() => cards as CardData[])
  const [currentIndex, setCurrentIndex] = useState(() => {
    const saved = UserPreferences.getCardActiveIndexVirtual()
    return saved >= 0 ? saved : 0
  })
  const [accelerating, setAccelerating] = useState(false)
  const [acceleratePopupVisible, setAcceleratePopupVisible] = useState(false)

  // 当前卡片
  const currentCard = localCards[currentIndex] || null

  // 当外部 cards 变化时同步更新 localCards（保留已获取的余额）
  useEffect(() => {
    setLocalCards(prevLocalCards => {
      return (cards as CardData[]).map(card => {
        // 如果本地已有该卡片的更新余额，保留它
        const existingCard = prevLocalCards.find(c => c.item_id === card.item_id)
        if (existingCard?.balance_limit?.balance !== card.balance_limit?.balance) {
          // 如果本地余额与新数据不同，且本地有更新过的余额，保留本地的
          if (existingCard?._balanceUpdated) {
            return { ...card, balance_limit: existingCard.balance_limit, _balanceUpdated: true }
          }
        }
        return card
      })
    })
  }, [cards])

  // 索引越界保底：卡注销导致列表变短时回到第 0 张
  useEffect(() => {
    if (localCards.length > 0 && currentIndex >= localCards.length) {
      setCurrentIndex(0)
      UserPreferences.setCardActiveIndexVirtual(0)
      swiperRef.current?.slideTo(0)
    }
  }, [localCards.length, currentIndex])

  // 用于存储当前请求的引用，以便取消
  const currentRequestRef = useRef<CancellableRequest<getVirtualCardDetailResponse> | null>(null)

  // 当前卡片的 item_id（用于触发余额获取）
  const currentCardId = localCards[currentIndex]?.item_id
  const currentCardIssued = localCards[currentIndex]?.card_issued

  // 切换卡片时获取最新余额（只依赖 currentIndex 和 currentCardId）
  useEffect(() => {
    if (!currentCardIssued || !currentCardId) {
      return
    }

    // 取消上一个未完成的请求
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel()
    }

    const itemId = currentCardId

    // 创建可取消的请求
    const request = createCancellablePost<getVirtualCardDetailResponse>(
      '/web/card/info',
      { item_id: Number(itemId), issued: true }
    )
    currentRequestRef.current = request

    request.promise
      .then((res) => {
        const balance = res?.card?.balance_limit?.balance
        if (balance !== undefined) {
          // 更新对应卡片的余额
          setLocalCards(prev => prev.map(c =>
            c.item_id === itemId
              ? { ...c, balance_limit: { ...c.balance_limit, balance }, _balanceUpdated: true }
              : c
          ))
        }
      })
      .catch((error: any) => {
        // 如果是取消请求，静默处理
        if (isRequestCancelled(error)) return
        console.error('获取卡片余额失败:', error)
      })
      .finally(() => {
        // 清除引用
        if (currentRequestRef.current === request) {
          currentRequestRef.current = null
        }
      })

    // 组件卸载时取消请求
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel()
        currentRequestRef.current = null
      }
    }
  }, [currentIndex, currentCardId, currentCardIssued])

  // 卡片切换回调（仅用户滑动时持久化索引，避免 mount 时用 0 覆盖已保存值）
  const handleCardChange = useCallback((_card: CardData, index: number) => {
    setCurrentIndex(index)
    UserPreferences.setCardActiveIndexVirtual(index)
  }, [])

  // 获取卡片ID
  const getCardId = (card: CardData | null): number => {
    if (!card) return 0
    return Number(card.item_id || 0)
  }

  // 获取掩码卡号
  const getMaskedPan = (card: CardData | null) => {
    if (!card) return ''
    return card.masked_pan || ''
  }

  // 判断卡片是否已下卡
  const isCardIssued = (card: CardData | null): boolean => {
    if (!card) return false
    return card.card_issued === true
  }

  // 判断卡片是否已冻结
  const checkCardFrozen = (card: CardData | null): boolean => {
    if (!card) return false
    return card.status === CardStatus.Frozen
  }

  // 当前卡片是否已下卡
  const isCurrentCardIssued = useMemo(() => {
    return isCardIssued(currentCard)
  }, [currentCard])

  // 当前卡片是否已冻结
  const isCurrentCardFrozen = useMemo(() => {
    return checkCardFrozen(currentCard)
  }, [currentCard])

  // 当前卡片是否 KYC 失败
  const isKycFailed = currentCard?.status === CardStatus.KycFailed
  // 判断当前卡片是否显示查询kyc审核状态按钮（未下卡且 KycPending 状态）
  const showAccelerate = !currentCard?.card_issued && currentCard?.status === CardStatus.KycPending

  const canAccelerate = useMemo(() => {
    return !!(currentCard as any)?.can_sync_kyc
  }, [currentCard])

  // 查询kyc审核状态
  const handleAccelerateCard = useCallback(async () => {
    if (!currentCard) return

    const { card_product_id, kyc_id, can_sync_kyc } = currentCard as any

    if (!can_sync_kyc) {
      new Error('当前暂不可查询kyc审核状态')
      return
    }

    if (!card_product_id || !kyc_id) {
      new Error('缺少必要信息暂时无法查询kyc审核状态')
      return
    }

    try {
      setAccelerating(true)
      await APIWeb.accelerateCardV2({
        card_product_id: Number(card_product_id),
        kyc_id: Number(kyc_id)
      })
      setAcceleratePopupVisible(true)
    } catch (error) {
      console.error('查询kyc审核状态失败:', error)
      // Toast.fail(t('查询kyc审核状态失败请稍后重试'))
    } finally {
      setAccelerating(false)
    }
  }, [currentCard, t])

  // 重新填写（KYC失败时）- 跳转到KYC编辑页面，标记为虚拟卡重提交流程
  const handleReapply = useCallback(() => {
    const kycId = (currentCard as any)?.kyc_id
    const applicationId = currentCard?.item_id
    if (!kycId) return
    navigate('/my/cardholder/edit', { state: { kycId, applicationId, isReapply: true } })
  }, [currentCard, navigate])

  // 获取当前卡片信息（用于传递给子页面）
  const getCurrentCardInfo = useCallback(() => {
    if (!currentCard) return {}
    return {
      maskedPan: currentCard.masked_pan,
      balance: currentCard.balance_limit?.balance ?? currentCard.available_balance,
      backgroundImage: currentCard.image,
      cardIssued: currentCard.card_issued,
      status: currentCard.status
    }
  }, [currentCard])

  // 操作按钮点击回调
  const handleActionClick = useCallback((actionType: CardActionType) => {
    if (!currentCard) return

    const cardId = getCardId(currentCard)
    const maskedPan = getMaskedPan(currentCard)
    const cardIssued = isCardIssued(currentCard)
    const cardInfo = getCurrentCardInfo()
    
    switch (actionType) {
      case CardActionType.Recharge:
        navigate(`/card-v2/recharge?id=${cardId}&cardNo=${maskedPan}`)
        break
      case CardActionType.Transfer:
        navigate(`/card-v2/card-withdraw?id=${cardId}&cardNo=${maskedPan}`, {
          state: cardInfo
        })
        break
      case CardActionType.Info:
        navigate(`/card-v2/card-info?id=${cardId}&issued=${cardIssued}`)
        break
      case CardActionType.Manage:
        navigate(`/card-v2/card-manage?id=${cardId}&cardNo=${maskedPan}&frozen=${isCurrentCardFrozen}`, {
          state: cardInfo
        })
        break
      default:
        break
    }
  }, [currentCard, navigate, isCurrentCardFrozen, getCurrentCardInfo])

  if (cards.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col pb-10">
      {/* 卡片轮播 */}
      <CardSwiper
        ref={swiperRef}
        cards={localCards}
        onCardChange={handleCardChange}
        initialIndex={currentIndex}
        activeIndex={currentIndex}
      />

      {/* 根据当前卡片状态显示不同内容 */}
      {currentCard && (
        isCurrentCardIssued ? (
          <>
            {/* 已下卡：显示操作按钮和交易记录 */}
            <CardActions
              onActionClick={handleActionClick}
              className="mt-4"
            />

            {/* 交易记录 */}
            <TransactionRecords
              cardId={getCardId(currentCard)}
              cardInfo={{
                cardImage: currentCard.image,
                cardNo: currentCard.masked_pan,
                cardStatus: currentCard.status,
                cardType: currentCard.form_factor
              }}
              className="mt-6"
            />
          </>
        ) : isKycFailed ? (
          <KycFailed onReapply={handleReapply} />
        ) : (
          /* 未下卡：显示提示文本，KycPending 状态显示查询kyc审核状态按钮 */
          <div className="mt-6 px-5 py-8 flex flex-col items-center justify-center">
            <div className="text-base text-[#86909C] mb-4 text-center">
              {t('正在下卡中请稍后刷新查看')}
            </div>
            {showAccelerate && (
              <CustomButton
                size='small'
                loading={accelerating}
                color={canAccelerate ? 'primary' : 'default'}
                disabled={!canAccelerate || accelerating}
                onClick={canAccelerate ? handleAccelerateCard : undefined}
              >
                {t('查询kyc审核状态')}
              </CustomButton>
            )}
          </div>
        )
      )}

      {/* 查询kyc审核状态成功弹窗 */}
      <AcceleratePopup
        visible={acceleratePopupVisible}
        onClose={() => setAcceleratePopupVisible(false)}
      />
    </div>
  )
}

export default ElectronicCard
